import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('idle');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9agdkijutvtlj0vt8kg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Something went wrong. Please try again.';
        if (typeof msg === 'string' && msg.toLowerCase().includes('spam')) {
          setStatus('success');
          setEmail('');
          return;
        }
        setStatus('error');
        setErrorMsg(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="relative bg-foreground-900 mx-3 md:mx-5 mb-3 md:mb-5 rounded-2xl overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.03]">
        <span className="font-heading text-[12vw] font-black text-white whitespace-nowrap">SUPERBUY SHEET</span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          <div className="lg:col-span-1">
            <Link to="/" className="font-heading text-2xl md:text-3xl font-bold text-white whitespace-nowrap cursor-pointer">
              SuperBuySheet
            </Link>
            <p className="text-white/50 text-sm mt-4 leading-relaxed max-w-xs">
              The world's most comprehensive Superbuy Spreadsheet resource. Helping international shoppers discover, compare, and shop smarter.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-5">Navigate</h4>
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
              <Link to="/article/what-is-superbuy-spreadsheet" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">Guides</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">Superbuy Sheet</a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-5">Popular Categories</h4>
            <div className="flex flex-col gap-3">
              <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">Shoes</a>
              <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">Hoodies/Sweaters</a>
              <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">T-Shirts</a>
              <a href="https://kaspreadsheet.com/search-921/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">Bags</a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-5">Stay Updated</h4>
            <p className="text-white/50 text-sm mb-4">Get the latest spreadsheet updates and shopping guides.</p>
            <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-3">
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 bg-white/10 border border-white/20 rounded-l-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/40 transition-colors"
                />
                <button
                  type="submit"
                  className="bg-primary-500 text-white rounded-r-full px-4 py-2.5 cursor-pointer hover:bg-primary-600 transition-colors flex items-center justify-center"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="antispam-field"
              />
              {status === 'success' && (
                <p className="text-primary-400 text-xs">Thank you for subscribing!</p>
              )}
              {status === 'error' && (
                <p className="text-red-400 text-xs">{errorMsg}</p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/35 text-xs text-center sm:text-left">
            &copy; 2026 SuperBuySheet. All rights reserved. We are an independent resource and not affiliated with Superbuy.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-white/35 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap">Privacy</Link>
            <Link to="/" className="text-white/35 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap">Disclaimer</Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-white/35 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap">
              Superbuy Sheet <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}